import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useDeleteUserMutation } from '../../api';
import { Button } from '../../components/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/Dialog';
import { useToaster } from '../../hooks/useToaster';
import { deleteUser as deleteAdminViewUser } from '../../stores/slices/admin-view';

type DeleteUserModalProps = {
  userId: string;
  email: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const DeleteUserModal = ({ open, onOpenChange, userId, email }: DeleteUserModalProps) => {
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const dispatch = useDispatch();
  const toast = useToaster();

  const onDelete = useCallback(async () => {
    try {
      await deleteUser(userId).unwrap();
      dispatch(deleteAdminViewUser({ id: userId }));
      onOpenChange(false);
      toast.success('User deleted successfully.');
    } catch {
      toast.error('Failed to delete user. Please try again.');
    }
  }, [deleteUser, userId, dispatch, onOpenChange, toast]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <DialogDescription>Are you sure you want to delete user {email}?</DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="danger" onClick={onDelete} disabled={isDeleting} isLoading={isDeleting}>
            Delete
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
